import type { Account, AccountKind } from "@/hooks/useAccounts";

/**
 * Resolves the canonical account "kind" used across the UI.
 *  - "sample" → system-generated demo data (is_mock = true)
 *  - "demo"   → broker demo account (real market, fake money)
 *  - "real"   → broker live account (real money)
 *  - "contest"→ broker contest account
 *
 * Resolution priority:
 *  1) is_mock === true → "sample"
 *  2) explicit account_type column (lowercased), if recognised
 *  3) heuristic on broker_server (contains "demo" / "contest" / "live" / "real")
 *  4) fallback → "demo"
 */
export function resolveAccountKind(account: Account): AccountKind {
  if (account.is_mock) return "sample";

  const t = (account.account_type ?? "").toString().toLowerCase().trim();
  if (t === "sample" || t === "demo" || t === "real" || t === "contest") {
    return t as AccountKind;
  }

  const server = (account.broker_server ?? "").toLowerCase();
  if (server.includes("contest")) return "contest";
  if (server.includes("demo")) return "demo";
  if (server.includes("live") || server.includes("real")) return "real";

  return "demo";
}

export interface AccountKindMeta {
  labelFa: string;
  labelEn: string;
  badgeFa: string;
  badgeEn: string;
  /** tailwind classes for solid colored chip */
  badgeClasses: string;
  /** tailwind classes for soft tinted card */
  tintClasses: string;
  /** dot color for status indicators */
  dotClass: string;
}

export const ACCOUNT_KIND_META: Record<AccountKind, AccountKindMeta> = {
  sample: {
    labelFa: "نمونه",
    labelEn: "Sample",
    badgeFa: "نمونه",
    badgeEn: "Sample",
    badgeClasses:
      "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
    tintClasses: "bg-amber-500/5 border-amber-500/20",
    dotClass: "bg-amber-500",
  },
  demo: {
    labelFa: "دمو بروکر",
    labelEn: "Demo",
    badgeFa: "دمو",
    badgeEn: "Demo",
    badgeClasses:
      "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30",
    tintClasses: "bg-sky-500/5 border-sky-500/20",
    dotClass: "bg-sky-500",
  },
  real: {
    labelFa: "حساب واقعی",
    labelEn: "Real",
    badgeFa: "واقعی",
    badgeEn: "Real",
    badgeClasses:
      "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
    tintClasses: "bg-emerald-500/5 border-emerald-500/20",
    dotClass: "bg-emerald-500",
  },
  contest: {
    labelFa: "مسابقه",
    labelEn: "Contest",
    badgeFa: "مسابقه",
    badgeEn: "Contest",
    badgeClasses:
      "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30",
    tintClasses: "bg-violet-500/5 border-violet-500/20",
    dotClass: "bg-violet-500",
  },
};

export function getAccountKindMeta(account: Account): AccountKindMeta {
  return ACCOUNT_KIND_META[resolveAccountKind(account)];
}
